import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, ChevronLeft, Loader2, Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type AppNotification,
} from '@/lib/api/notifications';
import { cn } from '@/lib/utils';
import {
  formatNotificationTimestamp,
  getNotificationPath,
  getNotificationSearchText,
  NotificationCategoryIcon,
} from '@/components/notifications/notificationPresentation';

const NOTIFICATION_CENTER_QUERY_KEY = ['notifications', 'center'];
const NOTIFICATION_PAGE_QUERY_KEY = ['notifications', 'page'];
const PAGE_SIZE = 8;

function getVisiblePageNumbers(currentPage: number, pageCount: number): number[] {
  if (pageCount <= 5) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const start = Math.min(Math.max(currentPage - 2, 1), pageCount - 4);
  return Array.from({ length: 5 }, (_, index) => start + index);
}

const NotificationsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);

  const notificationsQuery = useQuery({
    queryKey: NOTIFICATION_PAGE_QUERY_KEY,
    queryFn: () => getNotifications({ status: 'all', limit: 200 }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const notifications = notificationsQuery.data?.items ?? [];
  const unreadCount = notificationsQuery.data?.unreadCount ?? 0;
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredNotifications = React.useMemo(
    () =>
      normalizedSearch
        ? notifications.filter((notification) => getNotificationSearchText(notification).includes(normalizedSearch))
        : notifications,
    [normalizedSearch, notifications]
  );
  const pageCount = Math.max(1, Math.ceil(filteredNotifications.length / PAGE_SIZE));
  const visiblePageNumbers = getVisiblePageNumbers(currentPage, pageCount);
  const pagedNotifications = filteredNotifications.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  React.useEffect(() => {
    setCurrentPage(1);
  }, [normalizedSearch]);

  React.useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount]);

  const invalidateNotifications = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_PAGE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_CENTER_QUERY_KEY });
  }, [queryClient]);

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: invalidateNotifications,
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidateNotifications,
  });

  const openNotification = async (notification: AppNotification) => {
    if (!notification.isRead) {
      await markReadMutation.mutateAsync(notification.id).catch(() => null);
    }
    navigate(getNotificationPath(notification));
  };

  const markNotificationAsRead = async (
    event: React.MouseEvent<HTMLButtonElement>,
    notification: AppNotification
  ) => {
    event.preventDefault();
    event.stopPropagation();
    await markReadMutation.mutateAsync(notification.id).catch(() => null);
  };

  return (
    <div className="relative py-1 lg:ml-0.5 lg:mt-1.5">
      <section className="min-h-[calc(100vh-7.5rem)] overflow-hidden rounded-[28px] border border-[rgba(15,23,42,0.08)] bg-[#F7FAFB] p-4 sm:p-5 lg:p-6">
        <nav
          aria-label="breadcrumb"
          className="flex min-h-[38px] items-center rounded-[10px] border border-[#DDE6EA] bg-[#F8FAFB] px-5 text-sm"
        >
          <Link to="/dashboard" className="text-[#8D939A] transition-colors hover:text-[#1D2B2D]">
            Dashboards
          </Link>
          <span className="mx-6 text-[#D4D9DD]" aria-hidden="true">/</span>
          <span className="text-[#111827]" aria-current="page">Notifications</span>
        </nav>

        <div className="mt-9 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            aria-label="Back to dashboard"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#111827] transition-colors hover:bg-white"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>

          <div className="flex min-w-0 flex-1 items-center gap-3">
            <h1 className="truncate text-[32px] font-semibold leading-tight text-[#1D2B2D]">
              Notifications
            </h1>
            {unreadCount > 0 ? (
              <span className="relative inline-flex h-[30px] shrink-0 items-center rounded-[4px] bg-[#D9F5E5] px-2.5 text-[14px] font-medium leading-none text-[#4BD07E]">
                <span className="absolute -left-[3px] -top-[3px] h-2 w-2 rounded-full bg-[#4BD07E]" aria-hidden="true" />
                {unreadCount > 99 ? '99+' : unreadCount} New
              </span>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => markAllReadMutation.mutate()}
            disabled={unreadCount === 0 || markAllReadMutation.isPending}
            className="ml-auto inline-flex h-9 items-center gap-2 rounded-md px-2 text-sm font-medium text-[#09A7DA] transition-colors hover:bg-[#EFFBFF] disabled:cursor-not-allowed disabled:opacity-45"
          >
            {markAllReadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            <span>Mark all as read</span>
          </button>
        </div>

        <div className="mt-9 flex flex-wrap items-center justify-between gap-4 border-b border-[#E0E6EA] pb-3">
          <label className="relative block w-full max-w-[342px]">
            <span className="sr-only">Search notifications</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#AEB6BC]" aria-hidden="true" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search"
              className="h-8 w-full rounded-full border border-transparent bg-[#ECEFF0] px-9 pr-10 text-sm text-[#1D2B2D] outline-none transition-colors placeholder:text-[#AEB6BC] focus:border-[#CFE8F0] focus:bg-white"
            />
            <span className="pointer-events-none absolute right-2.5 top-1/2 inline-flex h-5 min-w-5 -translate-y-1/2 items-center justify-center rounded-md border border-[#D8DEE2] px-1 text-xs text-[#9AA3AA]">
              /
            </span>
          </label>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              aria-label="Previous notifications page"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#7D858C] transition-colors hover:bg-white hover:text-[#111827] disabled:cursor-not-allowed disabled:opacity-35"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            {visiblePageNumbers.map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                onClick={() => setCurrentPage(pageNumber)}
                className={cn(
                  'inline-flex h-8 min-w-8 items-center justify-center rounded-[10px] px-2 text-sm font-medium transition-colors',
                  pageNumber === currentPage
                    ? 'bg-white text-[#20AEDC]'
                    : 'bg-white/70 text-[#8A8F94] hover:bg-white hover:text-[#111827]'
                )}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              aria-label="Next notifications page"
              disabled={currentPage >= pageCount}
              onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#111827] transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-35"
            >
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="mt-7">
          {notificationsQuery.isLoading ? (
            <div className="flex min-h-56 items-center justify-center text-[#64748B]">
              <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
            </div>
          ) : pagedNotifications.length === 0 ? (
            <div className="flex min-h-56 items-center justify-center rounded-[14px] bg-white text-center text-sm text-[#64748B]">
              {normalizedSearch ? 'No notifications match your search.' : 'No notifications yet.'}
            </div>
          ) : (
            <ul className="space-y-3">
              {pagedNotifications.map((notification) => (
                <li key={notification.id}>
                  <div
                    className={cn(
                      'grid min-h-[78px] grid-cols-[52px_minmax(0,1fr)] items-center gap-4 rounded-[14px] bg-white px-5 py-3 transition-colors hover:bg-[#FCFEFF] sm:grid-cols-[52px_minmax(0,1fr)_auto]',
                      !notification.isRead && 'ring-1 ring-[#E3F3F8]'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => void openNotification(notification)}
                      className="contents text-left"
                    >
                      <NotificationCategoryIcon
                        notification={notification}
                        className="h-[44px] w-[44px] rounded-[8px]"
                        iconClassName="h-[21px] w-[21px]"
                        assessmentIconClassName="h-[27px] w-[27px]"
                        biomarkerIconClassName="h-[31px] w-[31px]"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-[15px] font-semibold leading-5 text-[#172027]">
                          {notification.title}
                        </span>
                        <span className="mt-1 block truncate text-[13px] leading-5 text-[#4F5661]">
                          {notification.body}
                        </span>
                      </span>
                    </button>

                    <span className="col-start-2 flex flex-col items-start gap-1 sm:col-start-auto sm:items-end">
                      <button
                        type="button"
                        onClick={(event) => void markNotificationAsRead(event, notification)}
                        disabled={markReadMutation.isPending}
                        className="text-[13px] font-medium leading-5 text-[#09A7DA] transition-colors hover:text-[#087CA1] disabled:cursor-not-allowed disabled:opacity-45"
                      >
                        Mark as read
                      </button>
                      <span className="whitespace-nowrap text-[13px] leading-5 text-[#4B525D]">
                        {formatNotificationTimestamp(notification.createdAt)}
                      </span>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
};

export default NotificationsPage;
